import {
  Room,
  User,
  getChatRoom,
  Notification,
  notificationConverter,
  roomConverter,
} from '@breakroom-nx/classes';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  getDoc,
  doc,
  onSnapshot,
} from 'firebase/firestore';

import {
  ref,
  set,
  onValue,
  query as dbQuery,
  orderByChild,
  push,
  runTransaction,
} from 'firebase/database';

import { auth } from '@breakroom-nx/apis';

import { RoomMember } from '@breakroom-nx/helpers';

import { firestore, database, functions } from '@breakroom-nx/apis';
import { httpsCallable } from 'firebase/functions';

import { ChatType, NotificationType } from '@breakroom-nx/helpers';

const apiName = '[CHAT API]';
export const chatAPI = createApi({
  reducerPath: 'chatApi',
  keepUnusedDataFor: 3600,
  baseQuery: fakeBaseQuery<Error>(),
  tagTypes: ['Chat', 'Invite'],
  endpoints: (builder) => ({
    // Get User's Chats
    getChatsForUser: builder.query<
      Room[],
      { uid?: string; chatType: ChatType }
    >({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'Chat' as const,
                id,
              })),
              'Chat',
            ]
          : ['Chat'],
      async queryFn(args) {
        try {
          const uid: string | undefined = args.uid;
          const type: ChatType = args.chatType;

          if (!uid) throw new Error(`Current User is Undefined`);

          // console.info(`${apiName}: Getting ${type} chats for ${uid}`);

          const chats: Room[] = [];

          const chatRef = collection(firestore, `users/${uid}/chats`);
          const typeQuery = query(
            chatRef,
            where('isDirect', '==', type === ChatType.Private),
            where('isInvite', '==', false)
          );

          const querySnapshot = await getDocs(typeQuery);
          const promises = querySnapshot.docs.map(async (doc) => {
            const chatID = doc.id;
            await getChatRoom(chatID, uid).then((room) => {
              if (room) {
                chats.push(room);
              }
            });
          });

          await Promise.all(promises);

          return { data: chats };
        } catch (err) {
          // console.log(
          //   `${apiName} (ERROR) Unable to get chats for undefined user: ${err}`
          // );
          throw new Error('Unable to get your chats at this time');
        }
      },
      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe;
        try {
          const uid: string | undefined = args.uid;
          const type: ChatType = args.chatType;

          if (!uid) throw new Error('UID cannot be undefined');

          await cacheDataLoaded.then((cache) => {
            // console.log(
            //   `Cache Data Loaded: [Chats Size: ${cache.data.length}]`
            // );
          });

          const chatRef = collection(firestore, `users/${uid}/chats`);
          const chatQueryRef = await query(
            chatRef,
            where('isDirect', '==', type === ChatType.Private),
            where('isInvite', '==', false)
          );

          unsubscribe = onSnapshot(chatQueryRef, async (snapshot) => {
            const chats: Room[] = [];
            const promises = snapshot.docs.map(async (doc) => {
              const chatID = doc.id;
              await getChatRoom(chatID, uid).then((room) => {
                if (room) {
                  chats.push(room);
                }
              });
            });

            await Promise.all(promises);

            updateCachedData(() => {
              return chats;
            });
          });
        } catch (err) {
          // console.log(`[USER API] (Error) Unable to update cache for user`);
        }

        await cacheEntryRemoved;
        unsubscribe && unsubscribe();
      },
    }),

    //! Mutations
    userJoinedChat: builder.mutation<
      null,
      { user: User | undefined; roomId: string }
    >({
      async queryFn({ user, roomId }) {
        try {
          if (!roomId || !user) {
            return { error: 'No user or room found' };
          }

          // console.log(
          //   `${apiName} Adding new chat ${roomId} to user's ${user.uid} collection`
          // );

          const joinChat = httpsCallable(functions, 'chat-join');

          await joinChat({
            roomID: roomId,
            userID: user.uid,
            username: user.username,
          })
            .then((response) => {
              const joinedSuccessfully = response.data;
              if (joinedSuccessfully) {
                return { data: null };
              } else {
                return { error: 'Unable to join room' };
              }
            })
            .catch((err) => {
              return { error: 'Unable to join room' };
            });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} (Error) Unable to join room: ${err}`);
          throw new Error('Failed to Join Room. Please try again.');
        }
      },
      invalidatesTags: ['Chat'],
    }),

    userLeftChat: builder.mutation<null, { user: User; roomId: string }>({
      async queryFn({ user, roomId }) {
        try {
          if (!roomId || !user) {
            return { error: 'No user or room' };
          }

          // console.log(`${apiName} User leaving chat ${roomId}`);

          const leaveChat = httpsCallable(functions, 'chat-leave');

          // console.log(
          //   `${apiName} Removing chat ${roomId} from user's ${user.uid} collection`
          // );

          await leaveChat({
            roomID: roomId,
            userID: user.uid,
            username: user.username,
          })
            .then((response) => {
              const leftSuccessfully = response.data;
              if (leftSuccessfully) {
                return { data: null };
              } else {
                return { error: 'No user or room' };
              }
            })
            .catch((err) => {
              return { error: 'No user or room' };
            });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error leaving the room`);
          throw new Error('Error leaving the room');
        }
      },
      invalidatesTags: ['Chat'],
    }),

    getChatNotificationsForUser: builder.query<Notification[], void>({
      async queryFn() {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) {
            throw new Error(`Current user's UID cannot be undefined`);
          }

          // console.info(`${apiName}: Getting chat notifications for ${uid}`);
          const chatNotifications: Notification[] = [];
          const userNotificationsPath = `users/${uid}/notifications`;
          const userNotificationsCollection = collection(
            firestore,
            userNotificationsPath
          ).withConverter(notificationConverter);

          const chatInvitesQuery = query(
            userNotificationsCollection,
            where('type', '==', NotificationType.Chat)
          );

          const querySnapshot = await getDocs(chatInvitesQuery);
          const promises = querySnapshot.docs.map(async (doc) => {
            const notification = doc.data();
            chatNotifications.push(notification);
          });

          await Promise.all(promises);

          return { data: chatNotifications };
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get chat notifications for user: ${err}`
          // );
          throw new Error('Error getting your chat notifications');
        }
      },
    }),

    getChatRoom: builder.query<Room | undefined, string>({
      async queryFn(roomId) {
        try {
          if (!roomId) throw new Error('Room ID cannot be undefined');

          // console.info(`${apiName}: Fetching room with id: ${roomId}`);

          const roomRef = doc(firestore, 'chats', roomId).withConverter(
            roomConverter
          );

          let retrievedRoom: Room | undefined = undefined;
          const docSnap = await getDoc(roomRef);

          if (docSnap.exists()) {
            // Convert to User Object
            const room = docSnap.data();

            // Get Members Data
            // console.info('Fetching Members for Room:', roomId);
            const membersPath = collection(roomRef, 'members');
            const membersSnap = await getDocs(membersPath);

            if (!membersSnap.empty) {
              const members: RoomMember[] = [];
              membersSnap.forEach((doc) => {
                const documentData = doc.data();
                const memberData: RoomMember = {
                  uid: doc.id,
                  flair: documentData.flair,
                };
                members.push(memberData);
              });

              room.members = members;
              retrievedRoom = room;
            }
          } else {
            // console.log('No such document!');
          }

          return { data: retrievedRoom };
        } catch (err) {
          // console.info(`${apiName}: Unable to retrieve room: ${err}`);
          throw new Error('Unable to retrieve room');
        }
      },
    }),

    getUnreadChatsCount: builder.query<number, null>({
      async queryFn() {
        try {
          const uid = auth.currentUser?.uid;

          // console.info(
          //   `${apiName}: Getting number of unread chats for client...`
          // );
          if (!uid) throw new Error(`Client UID is undefined`);

          const userChatPath = `users/${uid}/chats`;
          const userChatCollection = collection(firestore, userChatPath);
          const chatsQuery = query(
            userChatCollection,
            where('unreadMessage', '==', true)
          );

          const querySnapshot = await getDocs(chatsQuery);
          const unreadChatsSize = querySnapshot.size;
          // console.log(`${apiName}: Number of unread chats: ${unreadChatsSize}`);

          return { data: unreadChatsSize };
        } catch (err) {
          // console.log(
          //   `${apiName}: (ERROR) Unable to get number of unread chats: ${err}`
          // );
          throw new Error('Error getting the number of unread chats');
        }
      },
    }),
  }),
});

export const {
  useGetChatsForUserQuery,
  useUserJoinedChatMutation,
  useUserLeftChatMutation,
  useGetChatRoomQuery,
  // useGetChatNotificationsForUser,
  useGetUnreadChatsCountQuery,
} = chatAPI;
